<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  company: {
    type: Object,
  },
  missingFields: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['repeatCheck'])

const loading = ref(false)

const publisherUrl = computed(() => {
  if (props.company?.id) {
    return `${import.meta.env.VITE_PUBLISHER_URL}/companies/${props.company.id}/company`
  }
  return ''
})

function repeatCheck() {
  loading.value = true
  emit('repeatCheck')
}

watch(
  () => props.missingFields,
  () => {
    loading.value = false
  },
)
</script>

<template>
  <UCard
    variant="soft"
    class="mt-4"
    v-if="missingFields.length || loading"
    :ui="{
      root: 'bg-info-50 divide-y-0 border-2 border-info-400',
      header: 'sm:px-4',
      body: 'sm:py-0 sm:px-13',
    }"
  >
    <template #header>
      <div class="flex gap-2 items-center">
        <UIcon name="i-lucide-info" class="size-7 text-info-400" />
        <h3 class="font-medium">Some information is missing</h3>
      </div>
    </template>

    <div class="text-sm">
      <p class="mb-2">
        The site generation will work better if we have as much information as possible about
        <span class="font-medium">{{ company.name }}</span
        >. The following fields are currently missing:
      </p>
      <ul class="list-disc ml-4 mb-4">
        <li v-for="(field, index) in missingFields" :key="index" class="mb-2">
          <div class="flex items-start gap-x-3">
            <p class="text-sm/6 font-semibold">{{ field.label }}</p>
            <p
              v-if="field.important"
              class="mt-0.5 rounded-md bg-yellow-50 px-1.5 py-0.5 text-xs font-medium text-yellow-800 inset-ring inset-ring-yellow-600/20"
            >
              Important
            </p>
          </div>
          <div class="flex items-center gap-x-2 text-xs/5 text-gray-500">
            <p class="whitespace-nowrap">
              {{ field.description }}
            </p>
          </div>
        </li>
      </ul>
      <p class="mt-2">Click on the "Next" button again if you want to proceed anyway.</p>
    </div>

    <template #footer>
      <div class="flex gap-4">
        <UButton
          :disabled="loading"
          icon="i-lucide-external-link"
          color="info"
          :to="publisherUrl"
          target="_blank"
          >Go to Publisher</UButton
        >

        <UButton
          icon="i-lucide-refresh-cw"
          loading-icon="i-lucide-refresh-cw"
          :loading="loading"
          variant="outline"
          color="info"
          @click="repeatCheck()"
          >Check again</UButton
        >
      </div>
    </template>
  </UCard>
</template>
